import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { confirmPasswordReset, requestPasswordReset } from "../services/authService";

const OTP_RESEND_COOLDOWN_SECONDS = 300;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => (searchParams.get("email") || "").trim());
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [step, setStep] = useState("REQUEST");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const otpInputRef = useRef(null);
  const autoSubmitKeyRef = useRef("");

  const passwordChecks = {
    minLength: password.length >= 8,
    hasUpper: /[A-Z]/.test(password),
    hasLower: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[^A-Za-z0-9]/.test(password),
  };

  useEffect(() => {
    if (step !== "VERIFY" || otpCooldown <= 0) {
      return undefined;
    }
    const timer = setInterval(() => {
      setOtpCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [step, otpCooldown]);

  useEffect(() => {
    if (step === "VERIFY" && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [step]);

  const canSubmitVerify = (
    otp.trim().length === 6
    && password === passwordConfirm
    && Object.values(passwordChecks).every(Boolean)
  );

  const onSubmit = async (e, { skipPreventDefault = false } = {}) => {
    if (!skipPreventDefault && e?.preventDefault) {
      e.preventDefault();
    }
    setError("");
    setSuccess("");
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setError("Email is required.");
      return;
    }
    if (step === "VERIFY") {
      if (password !== passwordConfirm) {
        setError("Passwords do not match.");
        return;
      }
      if (!Object.values(passwordChecks).every(Boolean)) {
        setError("Password does not meet requirements.");
        return;
      }
    }
    setLoading(true);
    try {
      if (step === "REQUEST") {
        const { data } = await requestPasswordReset({ email: normalizedEmail });
        setEmail(normalizedEmail);
        setStep("VERIFY");
        setOtp("");
        setPassword("");
        setPasswordConfirm("");
        setOtpCooldown(OTP_RESEND_COOLDOWN_SECONDS);
        autoSubmitKeyRef.current = "";
        setSuccess(data?.detail || "If the email exists, an OTP was sent.");
      } else {
        const { data } = await confirmPasswordReset({
          email: normalizedEmail,
          otp: otp.trim(),
          password,
          password_confirm: passwordConfirm,
        });
        setSuccess(data?.detail || "Password reset successful.");
        autoSubmitKeyRef.current = "";
        window.setTimeout(() => navigate("/login", { replace: true }), 1200);
      }
    } catch (err) {
      const d = err.response?.data;
      const fieldPassword = Array.isArray(d?.password) ? d.password.join(" ") : d?.password;
      setError(
        d?.non_field_errors?.[0]
          || d?.otp?.[0]
          || d?.email?.[0]
          || d?.detail
          || fieldPassword
          || (typeof d === "string" ? d : null)
          || "Something went wrong. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Do not auto-submit while the user is still typing confirm password — only after a short settle.
    if (step !== "VERIFY" || loading || !canSubmitVerify) {
      return undefined;
    }
    const currentKey = `${email.trim().toLowerCase()}|${otp.trim()}|${password}|${passwordConfirm}`;
    if (autoSubmitKeyRef.current === currentKey) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      autoSubmitKeyRef.current = currentKey;
      onSubmit(null, { skipPreventDefault: true });
    }, 600);
    return () => window.clearTimeout(timer);
  }, [step, loading, canSubmitVerify, email, otp, password, passwordConfirm]);

  const onResendOtp = async () => {
    if (loading || otpCooldown > 0) {
      return;
    }
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const { data } = await requestPasswordReset({ email: normalizedEmail });
      setOtp("");
      setOtpCooldown(OTP_RESEND_COOLDOWN_SECONDS);
      autoSubmitKeyRef.current = "";
      if (otpInputRef.current) {
        otpInputRef.current.focus();
      }
      setSuccess(data?.detail || "New OTP sent.");
    } catch (err) {
      const d = err.response?.data;
      setError(
        d?.non_field_errors?.[0]
          || d?.email?.[0]
          || d?.detail
          || (typeof d === "string" ? d : null)
          || "Could not resend OTP."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrap">
      <form className="card auth-card stack" onSubmit={onSubmit} style={{ position: "relative" }}>
        <button
          type="button"
          className="close-btn"
          onClick={() => navigate("/")}
          aria-label="Close reset password"
        >
          ×
        </button>
        <h2>Reset password</h2>
        <p style={{ margin: 0, color: "var(--muted)" }}>
          {step === "REQUEST"
            ? "We send an OTP if the email is registered."
            : "Enter the OTP (expires in 10 minutes) and your new password."}
        </p>
        {error && <p className="msg msg-error">{error}</p>}
        {success && <p className="msg msg-success">{success}</p>}
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          disabled={step === "VERIFY"}
        />
        {step === "VERIFY" && (
          <>
            <input
              type="text"
              ref={otpInputRef}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              required
            />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <small style={{ color: "var(--muted)" }}>
                {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : "Didn't get the OTP?"}
              </small>
              <button
                type="button"
                onClick={onResendOtp}
                disabled={loading || otpCooldown > 0}
                style={{ width: "auto", padding: "8px 12px" }}
              >
                Resend OTP
              </button>
            </div>
            <input
              type="password"
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
            <div className="password-rules" aria-label="Password requirements">
              <div className={passwordChecks.minLength ? "password-rule met" : "password-rule"}>
                <span>{passwordChecks.minLength ? "✓" : "○"}</span>
                <span>At least 8 characters</span>
              </div>
              <div className={passwordChecks.hasUpper ? "password-rule met" : "password-rule"}>
                <span>{passwordChecks.hasUpper ? "✓" : "○"}</span>
                <span>At least one uppercase letter (A-Z)</span>
              </div>
              <div className={passwordChecks.hasLower ? "password-rule met" : "password-rule"}>
                <span>{passwordChecks.hasLower ? "✓" : "○"}</span>
                <span>At least one lowercase letter (a-z)</span>
              </div>
              <div className={passwordChecks.hasNumber ? "password-rule met" : "password-rule"}>
                <span>{passwordChecks.hasNumber ? "✓" : "○"}</span>
                <span>At least one number (0-9)</span>
              </div>
              <div className={passwordChecks.hasSpecial ? "password-rule met" : "password-rule"}>
                <span>{passwordChecks.hasSpecial ? "✓" : "○"}</span>
                <span>At least one special character</span>
              </div>
            </div>
          </>
        )}
        <button type="submit" disabled={loading}>
          {loading ? "Please wait..." : step === "REQUEST" ? "Send OTP" : "Verify OTP & reset password"}
        </button>
        <p style={{ margin: 0 }}>
          <Link to="/login">Back to sign in</Link>
          {" · "}
          <Link to="/signup">Create account</Link>
        </p>
        <p style={{ margin: 0, fontSize: 13 }} className="muted">
          <Link to="/">Website home</Link>
        </p>
      </form>
    </div>
  );
}
